/**
 * PropertySwitcher — header-bar dropdown for jumping between properties.
 *
 * Behavior:
 *   - When the URL is `/p/:propertyId/<layer>`, the trigger shows the
 *     current property's domain. Picking another property navigates to
 *     `/p/:newId/<same-layer>` so you stay in the same data layer.
 *   - When the URL is NOT a property workspace, the trigger says
 *     "Select property" and picking one jumps to /p/:id/overview.
 *   - Always has an "All properties" option that navigates to /properties.
 *
 * The single mental model: pick a property, keep doing what you were
 * doing. Same shape Ahrefs / SEMrush / GSC use.
 */
import { useNavigate, useLocation, useMatch } from "react-router-dom";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { ChevronDown, Globe, Check, Layers } from "lucide-react";
import { useProperties } from "@/lib/queries";
import { cn } from "@/lib/utils";

export function PropertySwitcher() {
  const nav = useNavigate();
  const loc = useLocation();
  const propMatch = useMatch("/p/:propertyId/*");
  const properties = useProperties(true);

  const currentId = propMatch ? Number(propMatch.params.propertyId) : null;
  const items = properties.data?.items ?? [];
  const current = items.find((p) => p.id === currentId);

  // Extract the data-layer tail (everything after /p/:id/) so we can
  // preserve it when switching properties — staying in "Crawl" when
  // jumping from nrg.ro to nws.ro is the right behavior.
  const tail = (() => {
    if (!propMatch) return "overview";
    const m = loc.pathname.match(/^\/p\/\d+\/([^/]+)/);
    return m?.[1] ?? "overview";
  })();

  const switchTo = (newId: number) => {
    nav(`/p/${newId}/${tail}`);
  };

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className="flex h-9 items-center gap-2 rounded-md border border-border bg-card px-3 text-sm font-medium hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring"
        >
          {current ? (
            <>
              <Globe className="h-4 w-4 text-primary" />
              <span className="font-mono">{current.domain}</span>
            </>
          ) : (
            <>
              <Layers className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Select property</span>
            </>
          )}
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={6}
          className="z-50 max-h-[60vh] w-72 overflow-y-auto rounded-md border border-border bg-card p-1 text-sm shadow-lg"
        >
          {/* Active properties */}
          {items
            .filter((p) => p.is_active)
            .map((p) => (
              <DropdownMenu.Item
                key={p.id}
                onSelect={() => switchTo(p.id)}
                className={cn(
                  "flex cursor-pointer items-center gap-2 rounded-sm px-3 py-2 outline-none transition-colors",
                  "data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground",
                  p.id === currentId && "bg-primary/5",
                )}
              >
                <Globe className={cn(
                  "h-3.5 w-3.5 shrink-0",
                  p.id === currentId ? "text-primary" : "text-muted-foreground",
                )} />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-mono text-xs">{p.domain}</div>
                  <div className="truncate text-[11px] text-muted-foreground">
                    {p.display_name}
                  </div>
                </div>
                {p.id === currentId && <Check className="h-3.5 w-3.5 text-primary" />}
              </DropdownMenu.Item>
            ))}

          {/* Inactive — collapsed under a separator if any */}
          {items.filter((p) => !p.is_active).length > 0 && (
            <>
              <DropdownMenu.Separator className="my-1 h-px bg-border" />
              <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                Inactive
              </div>
              {items
                .filter((p) => !p.is_active)
                .map((p) => (
                  <DropdownMenu.Item
                    key={p.id}
                    onSelect={() => switchTo(p.id)}
                    className="flex cursor-pointer items-center gap-2 rounded-sm px-3 py-2 text-muted-foreground outline-none data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground"
                  >
                    <Globe className="h-3.5 w-3.5 shrink-0 opacity-50" />
                    <span className="truncate font-mono text-xs opacity-75">{p.domain}</span>
                  </DropdownMenu.Item>
                ))}
            </>
          )}

          <DropdownMenu.Separator className="my-1 h-px bg-border" />

          <DropdownMenu.Item
            onSelect={() => nav("/properties")}
            className="flex cursor-pointer items-center gap-2 rounded-sm px-3 py-2 outline-none data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground"
          >
            <Layers className="h-3.5 w-3.5 text-muted-foreground" />
            All properties
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
